import { getClerkInstance } from '../auth/clerkAuth.js';

const STORAGE_KEY = 'tc_saved_indicator_groups';

// Default groups that ship with the app
export const DEFAULT_VT_GROUP = {
  id: 'default_vt',
  name: 'VT',
  description: 'Visual Trading indicator set',
  indicators: [
      {
        name: 'Donchian Channel',
        type: 'DC',
        side: 'onchart',
        settings: {
          group: 'VT',
          'z-index': 2,
          length: 20,
          topColor: '#ABEBC6',
          bottomColor: '#F5B7B1',
          color: '#A9A9A9'
        }
      },
      {
        name: 'VisualCandles',
        type: 'VisualCandles',
        side: 'onchart',
        settings: { group: 'VT' }
      },
      {
        name: 'Trend Color SMA (TSMA)',
        type: 'TSMA',
        side: 'onchart',
        settings: {
          lineWidth: 3,
          group: 'VT',
          'z-index': 1,
          length: 50
        }
      },
      {
        name: 'VT Target',
        type: 'VisualTrading',
        side: 'onchart',
        settings: {
          group: 'VT',
          'z-index': -3,
          smaLength: 50,
          dcLength: 20,
          riskRewardRatio: 3,
          showPendingTarget: true
        }
      },
      {
        name: 'VT Confidence',
        type: 'VTConfidence',
        side: 'onchart',
        settings: {
          group: 'VT',
          'z-index': 6,
          smaLength: 50,
          dcLength: 20,
          riskRewardRatio: 3,
          showDirection: true
        }
      },
      {
        name: 'MakersMove',
        type: 'MM',
        side: 'onchart',
        settings: {
          group: 'VT',
          'z-index': 3
        }
      },
      {
        name: 'TimeCycle',
        type: 'TimeCycle',
        side: 'onchart',
        settings: {
          group: 'VT',
          showCycleHigh: true,
          showCycleLow: true,
          showPendingCycleHigh: true,
          showPendingCycleLow: true,
          'z-index': -3
        }
      },
      {
        name: 'MTF Targets',
        type: 'MTFTargets',
        side: 'onchart',
        settings: {
          group: 'VT',
          'z-index': 10,
          position: 'top-right',
          showTable: true
        }
      },
      {
        name: 'LSR',
        type: 'LSR',
        side: 'onchart',
        settings: {
          group: 'VT',
          'z-index': 10,
          position: 'top-left',
          showPanel: true
        }
      },
      {
        name: 'Timeframe Trend',
        type: 'TimeframeTrend',
        side: 'onchart',
        settings: {
          group: 'VT'
        }
      }
  ],
  isDefault: true,
  createdAt: new Date('2024-01-01').toISOString(),
  updatedAt: new Date('2024-01-01').toISOString()
};

// Default groups that ship with the app
const DEFAULT_GROUPS = [DEFAULT_VT_GROUP];

export class IndicatorGroupsService {
  constructor() {
    this.clerkAuth = getClerkInstance();
  }

  // ── Read ──────────────────────────────────────────────

  async getIndicatorGroups() {
    try {
      // Try Clerk first if authenticated
      if (this.clerkAuth?.isAuthenticated) {
        const metadata = await this.clerkAuth.getPublicMetadata();
        const remote = metadata.indicatorGroups || [];
        if (remote.length) return this._mergeDefaults(remote);
      }
    } catch (error) {
      console.warn('Clerk unavailable, falling back to localStorage:', error.message);
    }

    // Fallback: localStorage
    return this._mergeDefaults(this._readLocal());
  }

  // ── Write ─────────────────────────────────────────────

  async saveIndicatorGroups(groups) {
    this._ensureDefaultGroup(groups);
    // Always persist to localStorage so it works offline / unauthenticated
    this._writeLocal(groups);

    // Also sync to Clerk when possible
    try {
      if (this.clerkAuth?.isAuthenticated) {
        await this.clerkAuth.updatePublicMetadata({ indicatorGroups: groups });
      }
    } catch (error) {
      console.warn('Could not sync groups to Clerk:', error.message);
    }

    return true;
  }

  // ── CRUD helpers ──────────────────────────────────────

  async addIndicatorGroup(group) {
    const groups = await this.getIndicatorGroups();
    const newGroup = {
      id: this.generateId(),
      name: group.name,
      description: group.description || '',
      indicators: group.indicators || [],
      isDefault: !!group.isDefault,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    if (newGroup.isDefault) {
      groups.forEach(g => { g.isDefault = false; });
    }
    groups.push(newGroup);
    await this.saveIndicatorGroups(groups);
    return newGroup;
  }

  async updateIndicatorGroup(groupId, updates) {
    const groups = await this.getIndicatorGroups();
    const index = groups.findIndex(g => g.id === groupId);

    if (index === -1) {
      throw new Error('Indicator group not found');
    }

    if (updates.isDefault) {
      groups.forEach(g => { g.isDefault = false; });
    }

    groups[index] = {
      ...groups[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };

    await this.saveIndicatorGroups(groups);
    return groups[index];
  }

  async deleteIndicatorGroup(groupId) {
    const groups = await this.getIndicatorGroups();
    const group = groups.find(g => g.id === groupId);
    if (group && group.isDefault) throw new Error('Default group cannot be deleted');

    const filteredGroups = groups.filter(g => g.id !== groupId);

    if (filteredGroups.length === groups.length) {
      throw new Error('Indicator group not found');
    }

    await this.saveIndicatorGroups(filteredGroups);
    return true;
  }

  async setDefaultIndicatorGroup(groupId) {
    const groups = await this.getIndicatorGroups();
    const index = groups.findIndex(g => g.id === groupId);

    if (index === -1) {
      throw new Error('Indicator group not found');
    }

    groups.forEach((group, i) => {
      group.isDefault = i === index;
      if (i === index) group.updatedAt = new Date().toISOString();
    });

    await this.saveIndicatorGroups(groups);
    return groups[index];
  }

  // ── Validation ────────────────────────────────────────

  validateIndicatorGroup(group) {
    if (!group.name || group.name.trim() === '') {
      throw new Error('Group name is required');
    }

    if (!Array.isArray(group.indicators)) {
      throw new Error('Indicators must be an array');
    }

    for (const indicator of group.indicators) {
      if (!indicator.type || !indicator.name) {
        throw new Error('Each indicator must have type and name');
      }
    }

    return true;
  }

  // ── Internals ─────────────────────────────────────────

  generateId() {
    return 'ig_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /** Merge default groups into the user list (avoids duplicates by id) */
  _mergeDefaults(userGroups) {
    userGroups = userGroups || [];
    const hasDefaultGroup = (group) =>
      group && (group.id === 'default_vt' || group.name === 'VT' || group.isDefault);
    const ids = new Set(userGroups.map(g => g.id));
    const merged = [...userGroups];
    
    for (const def of DEFAULT_GROUPS) {
      if (!ids.has(def.id) && !userGroups.some(hasDefaultGroup)) {
        merged.unshift(def); // first time: seed the default group
      } else {
        // The user already has this default group, possibly with indicators
        // they added or removed themselves (e.g. turned off Timeframe Trend).
        // Only de-duplicate their own list — never overwrite their choices
        // with the hardcoded DEFAULT_VT_GROUP, or every add/remove gets
        // silently reverted on the next load.
        const existingGroupIndex = merged.findIndex(g => hasDefaultGroup(g) || g.name === def.name);
        if (existingGroupIndex !== -1) {
          merged[existingGroupIndex] = this._removeDuplicateIndicators(merged[existingGroupIndex]);
        }
      }
    }
    
    if (!merged.some(group => group && group.isDefault === true)) {
      this._ensureDefaultGroup(merged);
    }
    return merged;
  }
  
  /** Remove duplicate indicators from a group based on type and name */
  _removeDuplicateIndicators(group) {
    if (!group || !Array.isArray(group.indicators)) {
      return group;
    }
    
    const seen = new Set();
    const uniqueIndicators = [];
    
    for (const indicator of group.indicators) {
      const key = `${indicator.type}:${indicator.name}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueIndicators.push(indicator);
      }
    }
    
    return { ...group, indicators: uniqueIndicators };
  }

  _ensureDefaultGroup(groups) {
    if (!Array.isArray(groups) || groups.some(group => group && group.isDefault === true)) return;
    const vt = groups.find(group => group && (group.id === 'default_vt' || group.name === 'VT'));
    if (vt) {
      vt.isDefault = true;
      return;
    }
    if (groups[0]) groups[0].isDefault = true;
  }

  _readLocal() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  _writeLocal(groups) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
    } catch (e) {
      console.warn('localStorage write failed:', e.message);
    }
  }
}

export default new IndicatorGroupsService();
